import { useLayoutEffect } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { BookOpen, ClipboardCheck, ClipboardList, Info, ListChecks, LogOut, Menu, Receipt } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { BrandLockup, BrandLogo } from "@/components/common/Brand";
import { UserAvatar } from "@/components/common/UserAvatar";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { NAV, ROLE_LABELS } from "@/lib/i18n";
import { cn } from "@/lib/utils";

const CountPill = ({ value, testId, active }) =>
  value ? (
    <span
      data-testid={testId}
      className={cn(
        "num ml-auto inline-flex min-w-[1.5rem] items-center justify-center rounded-md px-1.5 py-0.5 text-xs font-semibold",
        active ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground",
      )}
    >
      {value}
    </span>
  ) : null;

export const AppShell = () => {
  const { user, isAdmin, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Ny side skal alltid starte på toppen (før første teikning, så iOS Safari ikkje viser sida halvvegs nedscrolla).
  useLayoutEffect(() => {
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    window.scrollTo(0, 0);
  }, [location.pathname]);
  const { data: counts } = useQuery({
    queryKey: ["counts"],
    queryFn: async () => (await api.get("/orders/counts")).data,
    refetchInterval: 60000,
  });

  const sections = [
    {
      label: "Arbeid",
      items: [
        { to: "/mine-ordrar", label: NAV.mineOrdrar, icon: ClipboardList, count: counts?.mine_aktive, testId: "nav-mine-ordrar" },
        { to: "/ordrar", label: NAV.ordrar, icon: ListChecks, testId: "nav-ordrar" },
      ],
    },
  ];
  if (isAdmin) {
    sections.push({
      label: "Administrasjon",
      items: [
        { to: "/til-kontroll", label: NAV.tilKontroll, icon: ClipboardCheck, count: counts?.til_kontroll, testId: "nav-til-kontroll" },
        { to: "/fakturering", label: NAV.klarTilFakturering, icon: Receipt, count: counts?.klar_til_fakturering, testId: "nav-fakturering" },
        { to: "/register", label: NAV.register, icon: BookOpen, testId: "nav-register" },
      ],
    });
  }
  sections.push({ label: "Hjelp", items: [{ to: "/om-systemet", label: NAV.omSystemet, icon: Info, testId: "nav-om-systemet" }] });

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const mobileTabs = [
    { to: "/mine-ordrar", label: NAV.mineOrdrar, icon: ClipboardList, testId: "tab-mine-ordrar", count: counts?.mine_aktive },
    { to: "/ordrar", label: NAV.ordrar, icon: ListChecks, testId: "tab-ordrar" },
    { to: "/meir", label: NAV.meir, icon: Menu, testId: "tab-meir", count: isAdmin ? (counts?.til_kontroll || 0) + (counts?.klar_til_fakturering || 0) : 0 },
  ];
  const meirActive = ["/meir", "/til-kontroll", "/fakturering", "/register", "/om-systemet"].some((p) => location.pathname.startsWith(p));
  const roleLabel = ROLE_LABELS[user?.role] || user?.role;

  return (
    <TooltipProvider delayDuration={200}>
      <div className="flex min-h-screen bg-background">
        {/* Sidebar (desktop) */}
        <aside className="hidden w-[248px] shrink-0 flex-col border-r border-border bg-card md:flex lg:w-[272px]" data-testid="sidebar">
          <div className="flex h-16 items-center border-b border-border px-5">
            <BrandLogo className="w-[140px] lg:w-[150px]" data-testid="sidebar-logo" />
          </div>
          <nav className="flex flex-1 flex-col gap-5 overflow-y-auto px-3 py-4" aria-label="Hovudnavigasjon">
            {sections.map((section) => (
              <div key={section.label}>
                <p className="px-3 pb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{section.label}</p>
                <div className="flex flex-col gap-0.5">
                  {section.items.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      data-testid={item.testId}
                      className={({ isActive }) =>
                        cn(
                          "focus-ring relative flex min-h-[40px] items-center gap-3 rounded-md px-3 text-[13.5px] font-medium transition-colors duration-150",
                          isActive ? "bg-secondary text-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground",
                        )
                      }
                    >
                      {({ isActive }) => (
                        <>
                          <span className={cn("absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-full bg-primary", isActive ? "opacity-100" : "opacity-0")} aria-hidden="true" />
                          <item.icon className={cn("h-[18px] w-[18px] shrink-0", isActive ? "text-primary" : "")} />
                          <span className="truncate">{item.label}</span>
                          <CountPill value={item.count} testId={`${item.testId}-count`} active={isActive} />
                        </>
                      )}
                    </NavLink>
                  ))}
                </div>
              </div>
            ))}
          </nav>
          <div className="border-t border-border p-4">
            <div className="flex items-center gap-3">
              <UserAvatar name={user?.name} solid testId="sidebar-user-avatar" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold" data-testid="sidebar-user-name">{user?.name}</p>
                <p className="truncate text-xs text-muted-foreground" data-testid="sidebar-user-role">{roleLabel}</p>
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-10 w-10 shrink-0 text-muted-foreground hover:text-foreground" onClick={handleLogout} aria-label="Logg ut" data-testid="sidebar-logout-button">
                    <LogOut className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top" className="bg-popover text-popover-foreground">Logg ut</TooltipContent>
              </Tooltip>
            </div>
          </div>
        </aside>

        <div className="flex min-w-0 flex-1 flex-col">
          {/* Topbar – rullar med sida (ikkje sticky/fixed), så han aldri kan dekkje innhald øvst. Navigasjon ligg i botnlinja/sidemenyen. */}
          <header className="border-b border-border bg-card" data-testid="topbar">
            <div className="brand-rule md:hidden" aria-hidden="true" />
            <div className="flex h-14 items-center justify-between gap-3 px-4 md:px-6 lg:px-8">
              <div className="min-w-0 md:hidden">
                <BrandLockup compact testId="topbar-brand" />
              </div>
              <p className="hidden truncate text-sm text-muted-foreground md:block" data-testid="topbar-tagline">
                Internt ordresystem
              </p>
              <div className="flex shrink-0 items-center gap-2 md:gap-3">
                <div className="hidden text-right sm:block">
                  <p className="max-w-[180px] truncate text-sm font-semibold leading-tight" data-testid="topbar-user-name">{user?.name}</p>
                  <p className="text-xs leading-tight text-muted-foreground" data-testid="topbar-user-role">{roleLabel}</p>
                </div>
                <UserAvatar name={user?.name} solid className="md:hidden" testId="topbar-user-avatar" />
                <Button variant="ghost" size="icon" className="h-10 w-10 text-muted-foreground hover:text-foreground md:hidden" onClick={handleLogout} aria-label="Logg ut" data-testid="topbar-logout-button">
                  <LogOut className="h-5 w-5" />
                </Button>
              </div>
            </div>
          </header>

          <main className="mx-auto w-full max-w-7xl flex-1 px-4 pb-32 pt-5 md:px-6 md:pb-12 md:pt-8 lg:px-8" data-testid="main-content">
            <div className="fade-in-up" key={location.pathname}>
              <Outlet />
            </div>
          </main>
        </div>

        {/* Botn-navigasjon (mobil) */}
        <nav className="safe-bottom fixed inset-x-0 bottom-0 z-30 border-t border-border bg-card md:hidden" data-testid="bottom-nav" aria-label="Hovudnavigasjon (mobil)">
          <div className="flex h-16 items-stretch">
            {mobileTabs.map((tab) => {
              const active = tab.to === "/meir" ? meirActive : location.pathname.startsWith(tab.to);
              return (
                <NavLink
                  key={tab.to}
                  to={tab.to}
                  data-testid={tab.testId}
                  aria-current={active ? "page" : undefined}
                  className={cn(
                    "focus-ring relative flex min-h-[56px] min-w-[96px] flex-1 flex-col items-center justify-center gap-1 text-[11px] font-semibold transition-colors duration-150 active:bg-muted",
                    active ? "text-primary" : "text-muted-foreground",
                  )}
                >
                  <span className={cn("absolute top-0 h-[3px] w-10 rounded-b-full bg-primary", active ? "opacity-100" : "opacity-0")} aria-hidden="true" />
                  <span className="relative">
                    <tab.icon className="h-[22px] w-[22px]" />
                    {tab.count ? (
                      <span className="num absolute -right-2.5 -top-1.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold text-primary-foreground" data-testid={`${tab.testId}-count`}>
                        {tab.count}
                      </span>
                    ) : null}
                  </span>
                  <span>{tab.label}</span>
                </NavLink>
              );
            })}
          </div>
        </nav>
      </div>
    </TooltipProvider>
  );
};
